import { type SelectProps } from '.';
import {
  DropdownCellOptionType,
  type OptionGroup,
  type Option,
  DropdownCellOption,
  DropdownCellTitle,
} from '../../dropdown';
import Box from '@mui/material/Box';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import { type Theme } from '../../../../theme';

import type { JSX } from 'react';

const MENU_ITEM_MIN_HEIGHT = 40;

type SelectOptionsProps = Pick<SelectProps, 'options' | 'selected' | 'type'> & {
  theme: Theme;
};

const isOptionGroup = (option: Option | OptionGroup): option is OptionGroup =>
  'options' in option;

export const getSelectOptions = ({
  options = [],
  selected,
  type,
  theme,
}: SelectOptionsProps): JSX.Element[] => {
  return options.flatMap((option, index) =>
    isOptionGroup(option) ? (
      [
        <DropdownCellTitle title={option.title} key={option.title} />,
        ...option.options.map((groupOption) => (
          <MenuItem
            disabled={groupOption.disabled}
            value={groupOption.value}
            key={`${groupOption.value}-${index}`}
            sx={{
              opacity: 1,
              [`&.${menuItemClasses.root}`]: {
                padding: 0,
                minHeight: `${MENU_ITEM_MIN_HEIGHT}px`,
              },
              [`&.${menuItemClasses.disabled}`]: {
                pointerEvents: 'auto',
                backgroundColor: theme.radioButton.background,
              },
            }}
            disableGutters
            disableRipple
            disableTouchRipple
          >
            <DropdownCellOption
              disabled={groupOption.disabled}
              label={groupOption.label}
              sublabel={groupOption.sublabel}
              isSelected={groupOption.value === selected?.value}
              icon={
                type === DropdownCellOptionType.Icon ? groupOption.icon : null
              }
              iconAfter={groupOption.iconAfter}
              type={type}
            />
          </MenuItem>
        )),
        ...(index < options.length - 1
          ? [
              <Box
                sx={{
                  height: '1px',
                  backgroundColor: theme.popover.border,
                  margin: `${theme.spacing(1)} 0`,
                }}
              />,
            ]
          : []),
      ]
    ) : (
      <MenuItem
        disabled={option.disabled}
        value={option.value}
        key={`${index}`}
        sx={{
          opacity: `1 !important`,
          [`&.${menuItemClasses.root}`]: {
            padding: 0,
            minHeight: `${MENU_ITEM_MIN_HEIGHT}px`,
          },
          [`&.${menuItemClasses.disabled}`]: {
            pointerEvents: 'auto',
            backgroundColor: theme.radioButton.background,
          },
          [`&.${menuItemClasses.selected}`]: {
            backgroundColor: theme.menu.background.selected,
          },
        }}
        disableGutters
        disableRipple
        disableTouchRipple
      >
        <DropdownCellOption
          disabled={option.disabled}
          label={option.label}
          sublabel={option.sublabel}
          isSelected={option.value === selected?.value}
          icon={type === DropdownCellOptionType.Icon ? option.icon : null}
          iconAfter={option.iconAfter}
          type={type}
        />
      </MenuItem>
    ),
  );
};
